import type { AdjustmentType } from './enums.js';
import type { TransferChargeDimensions, TransferChargeSide } from './transfer-charges.js';

/**
 * Adjustments that settle with the payable: interest, penalty, IOF on the operation (a charge on
 * top of the payable, expensed to `AP_IOF`), discount, and operational rounding. Bank fees,
 * withholdings, and advances are not in this list.
 */
export const PAYABLE_SETTLEMENT_ADJUSTMENT_TYPES = [
  'JUROS',
  'MULTA',
  'IOF',
  'DESCONTO',
  'ARREDONDAMENTO',
] as const satisfies readonly AdjustmentType[];

export type PayableSettlementAdjustmentType = (typeof PAYABLE_SETTLEMENT_ADJUSTMENT_TYPES)[number];

/**
 * Financial adjustment within a reconciliation group
 * Represents fees, taxes, discounts, or other financial adjustments
 */
export interface ReconciliationAdjustmentResponse extends TransferChargeDimensions {
  /** Bank statement side evidencing a charge on a transfer. */
  transferChargeSide?: TransferChargeSide;
  /** Unique identifier */
  id: string;

  /** Parent reconciliation group ID */
  groupId: string;

  /** Type of adjustment */
  adjustmentType: AdjustmentType;

  /** Durable economic meaning for a historically overloaded rounding category. */
  sourceSemanticType?:
    'OPERATIONAL_ROUNDING' | 'PARTIAL_PAYMENT_REMAINDER' | 'OVERPAYMENT_EXCESS' | 'BOLETO_FRANCESA';

  /** Adjustment amount (positive or negative) */
  amount: number;

  /** Human-readable description */
  description: string;

  /** Whether to create an AP bill for this adjustment */
  createApBill: boolean;

  /** ID of the AP bill created (if applicable) */
  createdApBillId?: string;

  /** GL account ID for the adjustment */
  glAccountId?: string;

  /** Cost center ID */
  costCenterId?: string;

  /** Vendor ID (required for ADIANTAMENTO type) */
  vendorId?: string;

  /** Vendor name (resolved for display) */
  vendorName?: string;

  /** AP payment explicitly affected by this settlement adjustment. */
  paymentId?: string;

  /** Source reconciliation link that evidences the AP settlement adjustment. */
  reconciliationLinkId?: string;

  /**
   * Document this adjustment was applied to on the review screen, before the group exists.
   * Sent back as `CreateAdjustmentRequest.attributeToTargetId`. Absent on persisted responses.
   */
  attributeToTargetId?: string;

  /** When this adjustment was created */
  createdAt: string;

  /** When this adjustment was last updated */
  updatedAt: string;
}

/**
 * Request payload for creating an adjustment
 */
export interface CreateAdjustmentRequest extends TransferChargeDimensions {
  /** Required when explaining a bank-transfer amount difference. */
  transferChargeSide?: TransferChargeSide;
  /** Type of adjustment */
  adjustmentType: AdjustmentType;

  /** Explicit manual rounding; partial/excess markers are derived internally. */
  adjustmentSemanticType?: 'OPERATIONAL_ROUNDING';

  /** Adjustment amount */
  amount: number;

  /** ALC-4243: the same amount as an exact decimal string; wins over the float when present. */
  amountExact?: string;

  /** Description */
  description: string;

  /** Whether to create an AP bill (Accounts Payable) */
  createApBill?: boolean;

  /** Whether to create an AR invoice (Accounts Receivable) */
  createArInvoice?: boolean;

  /** Whether to create an internal transfer */
  createTransfer?: boolean;

  /** GL account ID (required if creating a record) */
  glAccountId?: string;

  /** Cost center ID */
  costCenterId?: string;

  /** Vendor ID (required for ADIANTAMENTO type when no bill is matched) */
  vendorId?: string;

  /** AP payment explicitly affected by this settlement adjustment. */
  paymentId?: string;

  /** Reconciliation link that evidences the AP settlement adjustment. */
  reconciliationLinkId?: string;

  /**
   * ALC-4243 / DECISIONS D4: the user's own justification for a withholding the source document
   * never declared. Persisted to `ReconciliationAdjustment.justificationNote`, which the
   * confirmation and posting paths read back. Deliberately not `description`, which is
   * auto-generated for engine rows and would satisfy D4 without anyone justifying anything.
   */
  justificationNote?: string;

  /** ALC-4243 / DECISIONS D2: how ISS withheld by the customer is treated. Absent = the default. */
  issTreatment?: 'RECOVERABLE' | 'OWN_LIABILITY';

  /**
   * ALC-4243: the target document this adjustment belongs to, resolved to its reconciliation
   * link after the group's links are created. For callers that create the group and its links in
   * one request, where no link id exists yet.
   */
  attributeToTargetId?: string;
}

/**
 * Suggested adjustment based on reconciliation analysis
 */
export interface AdjustmentSuggestion {
  /** Suggested adjustment type */
  type: AdjustmentType;

  /** Suggested amount */
  amount: number;

  /** Reason for the suggestion */
  reason: string;

  /** Confidence score (0-1) */
  confidence: number;

  /** Required GL account */
  glAccountId?: string;

  /** Suggested cost center */
  costCenterId?: string;
}

/**
 * Reusable adjustment template
 */
export interface AdjustmentTemplate {
  /** Unique identifier */
  id: string;

  /** Template name */
  name: string;

  /** Adjustment type */
  adjustmentType: AdjustmentType;

  /** Default GL account ID */
  defaultGlAccountId?: string;

  /** Default cost center ID */
  defaultCostCenterId?: string;

  /** Template description */
  description?: string;

  /** Whether template is active */
  isActive: boolean;

  /** Usage count */
  usageCount: number;

  /** When this template was created */
  createdAt: string;

  /** When this template was last updated */
  updatedAt: string;
}

/**
 * Response containing adjustment suggestions
 */
export interface AdjustmentSuggestionsResponse {
  /** List of suggested adjustments */
  suggestions: AdjustmentSuggestion[];

  /** Total amount of suggested adjustments */
  totalSuggestedAmount: number;

  /** Analysis context */
  analysisContext: {
    /** Origin amounts */
    originAmounts: number;

    /** Target amounts */
    targetAmounts: number;

    /** Current difference */
    difference: number;

    /** Currency */
    currency: string;
  };
}

/**
 * Query parameters for finding adjustments
 */
export interface AdjustmentQuery {
  /** Filter by reconciliation group */
  groupId?: string;

  /** Filter by adjustment type */
  adjustmentType?: AdjustmentType;

  /** Filter by amount range */
  minAmount?: number;

  /** Filter by amount range */
  maxAmount?: number;

  /** Filter by whether AP bill was created */
  hasApBill?: boolean;

  /** Filter by GL account */
  glAccountId?: string;

  /** Filter by cost center */
  costCenterId?: string;

  /** Search in description */
  search?: string;
}

/**
 * List response for adjustments
 */
export interface AdjustmentListResponse {
  /** List of adjustments */
  items: ReconciliationAdjustmentResponse[];

  /** Total count */
  total: number;

  /** Summary information */
  summary: {
    totalAmount: number;
    count: number;
    typesBreakdown: Record<AdjustmentType, number>;
  };
}

/**
 * Update request for adjustment
 */
export interface UpdateAdjustmentRequest {
  /** New amount */
  amount?: number;

  /** New description */
  description?: string;

  /** New GL account */
  glAccountId?: string;

  /** New cost center */
  costCenterId?: string;
}

/**
 * Adjustment calculation context
 */
export interface AdjustmentCalculationContext {
  /** Original transaction amount */
  originalAmount: number;

  /** Applied adjustments */
  adjustments: ReconciliationAdjustmentResponse[];

  /** Final reconciled amount */
  finalAmount: number;

  /** Whether amounts balance */
  isBalanced: boolean;

  /** Remaining difference */
  difference: number;
}
